// Per-vendor performance severity chart for the Vendor Portfolio's Vendor Performance card.
// One horizontal bar per vendor: length = latest business-owner score (0–5), color = the
// recommendation band the backend computes — so a red bar IS the vendor needing attention,
// sorted worst-first. Unreviewed vendors show as gray stubs after the scored ones, keeping
// coverage gaps visible without a gray bar masquerading as a real score.
// Band hues echo the table's badge colors (PERF_LABEL_CFG) and were re-stepped for
// distinguishability with the dataviz palette validator (all checks pass; the emerald
// contrast WARN is relieved by the per-bar score labels + the vendors table below).
import React from "react";
import {
    BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ReferenceLine, LabelList, ResponsiveContainer,
} from "recharts";

// Keys must match the labels the backend emits verbatim
// (VendorPerformanceScorecardCatalog.RecommendationLabel) — a mismatch renders the band gray.
const BAND_COLORS = {
    "High Concern":      "#b91c1c",
    "Needs Improvement": "#d97706",
    "Acceptable":        "#2563eb",
    "Strategic Vendor":  "#10b981",
    "Preferred Vendor":  "#15803d",
};
// Shortened labels for the compact legend + tooltip (the two "… Vendor" bands only).
const SHORT_BAND = { "Preferred Vendor": "Preferred", "Strategic Vendor": "Strategic" };
const shortBand = (label) => SHORT_BAND[label] || label;
const NOT_REVIEWED_COLOR = "#9ca3af";
const NOT_REVIEWED_STUB = 0.12; // just enough bar to hang the hover + label on
// 6 rows keeps the whole card at the sibling charts' height (row cap + compact stats below).
const MAX_ROWS = 6;
const ROW_HEIGHT = 26;

// Band label → legend order, worst first (mirrors the sort of the bars themselves).
const LEGEND = [
    { label: "High Concern",      color: BAND_COLORS["High Concern"] },
    { label: "Needs Improvement", color: BAND_COLORS["Needs Improvement"] },
    { label: "Acceptable",        color: BAND_COLORS["Acceptable"] },
    { label: "Strategic",         color: BAND_COLORS["Strategic Vendor"] },
    { label: "Preferred",         color: BAND_COLORS["Preferred Vendor"] },
];

const truncate = (name, max = 14) => (name.length > max ? `${name.slice(0, max)}…` : name);

const ScoreLabel = ({ x, y, width, height, value, index, rows }) => {
    const row = rows[index];
    if (!row) return null;
    return (
        <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11}
            fill={row.reviewed ? "#374151" : "#9ca3af"}
            fontStyle={row.reviewed ? "normal" : "italic"}
            style={{ fontVariantNumeric: "tabular-nums" }}>
            {row.reviewed ? row.score.toFixed(1) : "Not reviewed"}
        </text>
    );
};

const BandTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
            <div className="font-semibold text-gray-800 mb-0.5">{row.fullName}</div>
            {row.reviewed ? (
                <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: row.fill }} />
                    <span className="text-gray-600">{row.band} · <span className="font-semibold">{row.score.toFixed(1)}/5</span></span>
                </div>
            ) : (
                <div className="text-gray-400">No completed performance review yet</div>
            )}
        </div>
    );
};

export default function VendorPerformanceChart({ vendors, onVendorClick }) {
    const reviewed = vendors
        .filter(v => v.performanceScore != null)
        .map(v => ({
            vendorId: v.vendorId,
            fullName: v.vendorName,
            name: truncate(v.vendorName),
            score: Number(v.performanceScore),
            value: Number(v.performanceScore),
            band: shortBand(v.performanceRecommendationLabel),
            fill: BAND_COLORS[v.performanceRecommendationLabel] || NOT_REVIEWED_COLOR,
            reviewed: true,
        }))
        .sort((a, b) => a.score - b.score); // worst first — the problem vendor is the top bar

    const unreviewed = vendors
        .filter(v => v.performanceScore == null)
        .map(v => ({
            vendorId: v.vendorId,
            fullName: v.vendorName,
            name: truncate(v.vendorName),
            score: null,
            value: NOT_REVIEWED_STUB,
            band: null,
            fill: NOT_REVIEWED_COLOR,
            reviewed: false,
        }));

    const rows = [...reviewed, ...unreviewed].slice(0, MAX_ROWS);
    const hiddenCount = reviewed.length + unreviewed.length - rows.length;
    if (rows.length === 0) return null;

    const usedBands = new Set(reviewed.map(r => r.band));
    const legend = LEGEND.filter(b => usedBands.has(b.label));

    return (
        <div>
            <ResponsiveContainer width="100%" height={rows.length * ROW_HEIGHT + 24}>
                <BarChart data={rows} layout="vertical" margin={{ top: 2, right: 74, bottom: 0, left: 0 }}
                    barCategoryGap={3}>
                    <XAxis type="number" domain={[0, 5]} ticks={[1, 2, 3, 4, 5]}
                        tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} height={16} />
                    <YAxis type="category" dataKey="name" width={100}
                        tick={{ fontSize: 11, fill: "#374151" }} axisLine={false} tickLine={false} />
                    <ReferenceLine x={3} stroke="#9ca3af" strokeDasharray="4 3" />
                    <Tooltip content={<BandTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="value" barSize={13} radius={[0, 4, 4, 0]} isAnimationActive={false}
                        onClick={(row) => onVendorClick && row?.vendorId && onVendorClick(row.vendorId)}>
                        {rows.map(row => (
                            <Cell key={row.vendorId} fill={row.fill} cursor={onVendorClick ? "pointer" : "default"} />
                        ))}
                        <LabelList content={<ScoreLabel rows={rows} />} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {/* Band legend + threshold note — identity is never color-alone. Single line so the
                card stays at its siblings' height; the +N overflow note folds in here too. */}
            <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap">
                {legend.map(b => (
                    <span key={b.label} className="flex items-center gap-1 text-[10px] text-gray-500">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ background: b.color }} />
                        {b.label}
                    </span>
                ))}
                {unreviewed.length > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ background: NOT_REVIEWED_COLOR }} />
                        Not reviewed
                    </span>
                )}
                {hiddenCount > 0 && (
                    <span className="text-[10px] text-gray-400">+{hiddenCount} more below</span>
                )}
                <span className="text-[10px] text-gray-400 ml-auto">┊ 3.0 = Acceptable</span>
            </div>
        </div>
    );
}
